import { useEffect, useRef, useState } from 'react';
import {
  PageShell, PageContent,
  Button, Textarea, Card, Alert,
} from '@00start/ui';
import { useChat, type ChatDomain } from '../hooks/useChat';
import { DomainPicker } from '../components/DomainPicker';
import { ChatMessage } from '../components/ChatMessage';
import { BrandStrip } from '../components/BrandStrip';

// Curated prompts per domain — give the stakeholder a single click to a
// question that reliably returns a strong, cite-heavy answer. Each is
// known to match the deterministic corpus or produce a high-confidence
// LLM response with sources attached.
const SUGGESTED_PROMPTS: Record<ChatDomain, string[]> = {
  'faa-part145': [
    'What are the FAA Part 145 airframe rating requirements?',
    'When does a repair station need a capabilities list under 14 CFR 145.215?',
    'Summarize certifying-staff requirements for a Part 145 repair station.',
  ],
  'faa-part121': [
    'Is ETOPS pre-departure service check required under Part 121?',
    'What inspection programs must a Part 121 carrier maintain?',
    'Explain the CAMP requirements for Part 121 operators.',
  ],
  'easa-part145': [
    'What does EASA Part-145 require for certifying staff competence assessment?',
    'Compare EASA Part-145 to FAA Part 145 for airframe maintenance approvals.',
    'When is occurrence reporting under Regulation (EU) 376/2014 mandatory?',
  ],
  'icao-annex6': [
    'What continuing airworthiness requirements does ICAO Annex 6 impose?',
    'Summarize ICAO Doc 9760 applicability for air operators.',
    'Which ICAO Annex 6 provisions cover maintenance programs?',
  ],
};

export function ChatPage() {
  const { domain, messages, isStreaming, setDomain, send, cancel, clear } = useChat();
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages / streaming tokens.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isStreaming) return;
    send(input);
    setInput('');
  };

  const onSuggested = (prompt: string) => {
    if (isStreaming) return;
    // Send directly for zero-click feel during the demo; populating the input
    // and requiring a second click added friction the stakeholder wouldn't
    // want in the middle of explaining the product.
    send(prompt);
  };

  return (
    <PageShell
      breadcrumbs={<BrandStrip context="Aviation • Ask the Code" />}
      title="ASK THE CODE"
      subtitle="Streaming answers grounded in real regulatory corpora. Sources attached."
      action={
        <div className="flex gap-2">
          <Button
            onClick={() => { window.location.hash = ''; }}
            data-testid="aviation-demo-chat-back-button"
          >
            ← Demo
          </Button>
          <Button
            onClick={clear}
            disabled={isStreaming || messages.length === 0}
            data-testid="aviation-demo-chat-clear-button"
          >
            Clear
          </Button>
        </div>
      }
    >
      <PageContent>
        <div
          className="grid gap-4"
          data-testid="aviation-demo-chat-root"
        >
          <Card padding="md" header="Regulatory domain">
            <DomainPicker value={domain} onChange={setDomain} disabled={isStreaming} />
          </Card>

          <Card padding="md" header="Suggested prompts">
            <div
              className="flex flex-wrap gap-2"
              data-testid="aviation-demo-chat-suggested-prompts"
            >
              {SUGGESTED_PROMPTS[domain].map((prompt, i) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => onSuggested(prompt)}
                  disabled={isStreaming}
                  data-testid={`aviation-demo-chat-suggested-${i}`}
                  className={[
                    'px-3 py-2 rounded-xl text-sm text-left',
                    'bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm',
                    'border border-gray-200 dark:border-slate-700',
                    'hover:bg-gray-50 dark:hover:bg-slate-800',
                    'focus:outline-none focus:ring-2 focus:ring-sky-500',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    'text-gray-700 dark:text-slate-300',
                  ].join(' ')}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </Card>

          <Card
            padding="none"
            header={
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                  Conversation
                </h3>
                <span className="text-xs uppercase tracking-wider text-gray-500 dark:text-slate-400 font-mono">
                  {domain}
                </span>
              </div>
            }
          >
            <div
              ref={scrollRef}
              data-testid="aviation-demo-chat-transcript"
              className="max-h-[32rem] overflow-y-auto p-4 space-y-3"
            >
              {messages.length === 0 ? (
                <div
                  className="text-sm text-gray-500 dark:text-slate-400 text-center py-8"
                  data-testid="aviation-demo-chat-empty-state"
                >
                  Pick a suggested prompt above or type your own question below.
                </div>
              ) : (
                messages.map((m, i) => (
                  <ChatMessage key={m.id} message={m} index={i} />
                ))
              )}
            </div>

            <form
              onSubmit={onSubmit}
              className="border-t border-gray-100 dark:border-slate-800 p-4 flex gap-2 items-end"
              data-testid="aviation-demo-chat-form"
            >
              <div className="flex-1">
                <Textarea
                  value={input}
                  onChange={(v) => setInput(v)}
                  placeholder="Ask about FAA, EASA, or ICAO aviation maintenance regulations…"
                  rows={2}
                  disabled={isStreaming}
                  onKeyDown={(e) => {
                    // Enter submits; Shift+Enter inserts newline.
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onSubmit(e);
                    }
                  }}
                  data-testid="aviation-demo-chat-input"
                />
              </div>
              {isStreaming ? (
                <Button
                  type="button"
                  onClick={cancel}
                  data-testid="aviation-demo-chat-cancel-button"
                >
                  Stop
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={!input.trim()}
                  data-testid="aviation-demo-chat-send-button"
                >
                  Send
                </Button>
              )}
            </form>
          </Card>

          <Alert variant="info">
            Streaming responses come from Cloudflare Workers AI (Llama 3.3 70B)
            grounded in a verified regulatory corpus. Every answer includes
            its source documents. The ICAO Annex 6 corpus is gated until
            institutional access is confirmed — ask about FAA or EASA for
            full provenance.
          </Alert>
        </div>
      </PageContent>
    </PageShell>
  );
}
